import Vue from 'vue'
import Vuex from 'vuex'
import { auth } from './auth.module'
import ApiService from '../services/api.service'
import VueI18n from '@/i18n'
import { getI18nDateTimeFormat } from '@/lib/i18nDateTimeFormat'
import DashboardService from '@/services/dashboard.service'
import UserService from '@/services/user.service'
import { TokenService } from '@/services/storage.service'
import router from '@/router'
import { useTimeline } from '@/use/useTimeline'
import { useImageRequest } from '@/use/useImageRequest'
import { useTeamApplications } from '@/use/useTeamApplications'

Vue.use(Vuex)

const store = new Vuex.Store({
  modules: {
    auth
  },
  state: {
    finishedGames: true,
    loading: true,
    isNavOpened: false,
    isMiniNavOpened: false,
    config: null,
    disabledFeatures: [],
    settings: null,
    ytThumbnails: null,
    user: {
      name: '',
      clients: []
    },
    currentSeason: {},
    domains: {},
    seasonOptions: [],
    availableLanguages: [],
    language: '',
    currentClient: null,
    impersonatedUser: TokenService.isImpersonating()
      ? JSON.parse(localStorage.getItem('impersonated_user') || 'null')
      : null
  },
  mutations: {
    loading (state) {
      state.loading = true
    },
    loaded (state) {
      state.loading = false
    },
    setCurrentSeason (state, season) {
      state.currentSeason = season
    },
    async currentUser (state, user) {
      const adminClients = user.isSuperAdmin
        ? (user.clients ?? [])
        : (user.clients ?? []).filter(c => ['ADMIN', 'OWNER'].includes(c.role))

      if (!user.isSuperAdmin && !adminClients.length) {
        UserService.logout()
        return
      }

      state.user = { ...user, clients: adminClients }
      state.currentSeason = user.currentSeason

      let client = null

      if (user.currentClientId) {
        client = adminClients.find(c => c.uuid === user.currentClientId)
      }

      if (!client) {
        client = adminClients.find(c => c.uuid === localStorage.getItem('le-client')) ?? adminClients[0]
      }

      return await this.dispatch('setClient', client)
    },
    updateClient (state, data) {
      const { fetchCurrentTimeline } = useTimeline()
      const { fetchImageRequestsCount } = useImageRequest()
      const { fetchPendingTeamApplicationsCount } = useTeamApplications()

      this.dispatch('fetchFinishedGames')
      fetchCurrentTimeline()
      fetchImageRequestsCount()
      fetchPendingTeamApplicationsCount()

      state.currentSeason = data.currentSeason
      state.config = data.config
      state.disabledFeatures = data.disabledFeatures ?? []
      state.settings = data.settings

      for (const lang of state.availableLanguages) {
        VueI18n.setDateTimeFormat(lang.id, getI18nDateTimeFormat(state.settings.formatOfTime === '12h-clock'))
      }

      state.domains = data.domains
    },
    setSeasonOptions (state, data) {
      state.seasonOptions = data
    },
    setImpersonatedUser (state, user) {
      state.impersonatedUser = user
      if (user) {
        localStorage.setItem('impersonated_user', JSON.stringify(user))
      } else {
        localStorage.removeItem('impersonated_user')
      }
    }
  },
  getters: {
    isOwnerOfSomeOrganisations: state => state.user?.isOwner,
    isOwner: state => state.currentClient?.role === 'OWNER' || state.user?.isSuperAdmin,
    isAdmin: state => state.currentClient?.role === 'ADMIN' || state.currentClient?.role === 'OWNER' || state.user?.isSuperAdmin,
    loading: state => state.loading,
    disabledFeatures: state => (state.disabledFeatures ?? []).map(f => f.key)
  },
  actions: {
    async setClient ({ state }, client) {
      const { timeline } = useTimeline()
      const { imageRequestsCount } = useImageRequest()

      timeline.value = null
      imageRequestsCount.value = null

      state.currentClient = client
      localStorage.setItem('le-client', client.uuid)
      ApiService.setClient(client.uuid)

      await this.dispatch('getClient')

      return client
    },
    getClient ({ commit }) {
      store._configPromise = ApiService.get('api/v2/admin/client/config').then(response => {
        commit('updateClient', response.data)
      })
      return store._configPromise
    },
    getAvailableLanguages ({ state }) {
      state.availableLanguages = require('../config/languages.json')
    },
    initLanguage (store) {
      let lang

      if (localStorage.getItem('locale')) {
        lang = localStorage.getItem('locale')
      } else {
        lang = 'en'
      }

      store.dispatch('setLanguage', lang)
    },
    setLanguage ({ state }, lang) {
      state.language = lang
      VueI18n.locale = lang
      localStorage.setItem('locale', lang)
    },
    fetchFinishedGames ({ state }) {
      DashboardService.finishedGames().then(res => {
        state.finishedGames = res.data
      })
    },
    toggleMiniNav ({ state }) {
      state.isMiniNavOpened = !state.isMiniNavOpened
    },
    toggleNav ({ state }) {
      state.isNavOpened = !state.isNavOpened
    },
    closeNav ({ state }) {
      state.isNavOpened = false
    },
    getSeasonOptions ({ commit }, q = '') {
      const search = q || ''
      ApiService.get(`api/v1/admin/search/seasons?search=${search}`).then(response => commit('setSeasonOptions', response.data))
    },
    async createHandoff (_, memberUuid) {
      const response = await ApiService.postWithoutLoading(`api/super-admin/users/${memberUuid}/handoff`)
      return response.data
    },
    async startImpersonation ({ commit }, { memberUuid, memberName }) {
      const response = await ApiService.postWithoutLoading(`api/super-admin/users/${memberUuid}/impersonate`)
      const impersonationToken = response.data.access_token

      TokenService.saveOriginalToken(TokenService.getToken())
      TokenService.saveToken(impersonationToken)
      ApiService.setHeader()

      commit('setImpersonatedUser', { uuid: memberUuid, name: memberName })

      const userResponse = await UserService.getUser()
      await commit('currentUser', userResponse.data)

      router.push('/')
    },
    async stopImpersonation ({ commit }) {
      const originalToken = TokenService.getOriginalToken()
      TokenService.saveToken(originalToken)
      TokenService.removeOriginalToken()
      ApiService.setHeader()

      commit('setImpersonatedUser', null)

      const userResponse = await UserService.getUser()
      await commit('currentUser', userResponse.data)

      router.push('/')
    }
  }
})

export default store
export const useStore = () => store
